import {
  Flex,
  Text,
  useColorModeValue,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Switch,
  Stack,
  Box,
  Select,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { columnsDataEmailTemplate } from 'variables/columnsData';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { totalRow } from 'state/roles-permissions';
import { AddIcon } from '@chakra-ui/icons';
import { BsSearch } from 'react-icons/bs';
import { RxReset } from 'react-icons/rx';
import { useNotification } from 'hooks/useNotification';
import { listEmailTemplateAtom } from 'state/show';
import SearchTable2 from 'components/Tables/SearchTable2';
import { useHistory } from 'react-router-dom';
import { checkForbidden } from 'helpers/checkForbidden';

const STATUS_OPTIONS = [
  { id: '', name: 'All' },
  { id: 'Active', name: 'Active' },
  { id: 'InActive', name: 'InActive' },
];

function DataTableEmailTemplate() {
  const textColor = useColorModeValue('gray.700', 'white');
  const listEmailTemplate = useRecoilValue(listEmailTemplateAtom);
  const totalRows = useRecoilValue(totalRow);
  const notificationActions = useNotification();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCreate, setIsCreate] = useState(true);
  const [currentTemplateID, setCurrentTemplateID] = useState();
  const [isFetching, setIsFetching] = useState(false);
  const [newListTemplates, setNewListTemplates] = useState([]);
  const [refreshPagingDefault, setRefreshPagingDefault] = useState(false);
  const [paramTags, setParamTags] = useState([]);
  const [paramInput, setParamInput] = useState('');
  const history = useHistory();
  const toast = useToast();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('This field is required'),
    code: Yup.string().required('This field is required'),
  });
  const formOptions = { resolver: yupResolver(validationSchema) };
  const { register, handleSubmit, formState, reset, setValue } = useForm(formOptions);
  const {
    register: registerSearch,
    handleSubmit: handleSubmitSearch,
    watch: watchSearch,
    reset: resetSearch,
  } = useForm();
  const { errors, isSubmitting } = formState;

  const nameSearch = watchSearch('nameSearch');
  const statusSearch = watchSearch('statusSearch');

  useEffect(() => {
    if (listEmailTemplate) {
      const newList = listEmailTemplate.map((i) => ({
        ...i,
        filters: { name: nameSearch, status: statusSearch },
      }));
      setNewListTemplates(newList);
    }
  }, [listEmailTemplate]);

  const handleAddParam = () => {
    const trimmed = paramInput.trim();
    if (trimmed && !paramTags.includes(trimmed)) {
      setParamTags([...paramTags, trimmed]);
    }
    setParamInput('');
  };

  const handleParamInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddParam();
    }
  };

  const handleRemoveParam = (key) => {
    setParamTags(paramTags.filter((t) => t !== key));
  };

  async function onSubmitSearch({ nameSearch, statusSearch }) {
    setIsFetching(true);
    await notificationActions.getListEmailTemplate(
      { name: nameSearch, status: statusSearch },
      0,
      localStorage && localStorage.getItem('currentPageSizeStore')
        ? localStorage.getItem('currentPageSizeStore')
        : 10
    );
    setRefreshPagingDefault(true);
    setIsFetching(false);
  }

  const recallApi = async (fetchDataOptions) => {
    setIsFetching(true);
    await notificationActions.getListEmailTemplate(
      {
        name: nameSearch,
        status: statusSearch || 'Active',
        sort_type: fetchDataOptions.sort_type,
        sort_field: fetchDataOptions.sort_field,
      },
      fetchDataOptions.pageIndex,
      fetchDataOptions.pageSize
    );
    setIsFetching(false);
  };

  async function onSubmit({ name, code, isActive }) {
    const params = {
      name,
      code,
      status: isActive ? 'Active' : 'InActive',
      params: paramTags.join(','),
    };
    try {
      let res;
      if (isCreate) {
        res = await notificationActions.createEmailTemplate(params);
      } else {
        delete params.code;
        res = await notificationActions.updateEmailTemplate(params, currentTemplateID);
      }

      if (res.status === 200) {
        onClose();
        reset();
        const message = isCreate
          ? 'Create Email Template success'
          : 'Update Email Template success';

        toast({
          description: message,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        await notificationActions.getListEmailTemplate(
          {
            name: nameSearch,
            status: statusSearch,
          },
          0,
          localStorage && localStorage.getItem('currentPageSizeStore')
            ? localStorage.getItem('currentPageSizeStore')
            : 10
        );
      }
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function editTemplate(templateID) {
    history.push(`/admin/management/email-template-detail/${templateID}`);
  }

  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' display='block'>
          <Text
            float='left'
            color={textColor}
            fontSize='lg'
            fontWeight='bold'
            mb='6px'
          >
            Email Template Management
          </Text>
          <Stack float='right' direction='row' spacing={4}>
            {checkForbidden(133) && (
              <Button
                size='sm'
                onClick={() => {
                  onOpen();
                  setIsCreate(true);
                  reset();
                  setParamTags([]);
                  setParamInput('');
                }}
                leftIcon={<AddIcon />}
                colorScheme='blue'
                variant='outline'
              >
                Add Template
              </Button>
            )}
          </Stack>
          <Modal
            closeOnOverlayClick={false}
            isOpen={isOpen}
            onClose={() => {
              onClose();
              reset();
            }}
            size='2xl'
          >
            <ModalOverlay />
            <form onSubmit={handleSubmit(onSubmit)}>
              <ModalContent>
                <ModalHeader>
                  {isCreate ? 'Add New Email Template' : 'Edit Email Template'}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                  <FormControl isRequired>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Code
                    </FormLabel>
                    <Input
                      fontSize='sm'
                      ms='4px'
                      type='text'
                      placeholder='Code'
                      maxLength={250}
                      mb={errors.code?.message ? '5px' : { lg: '24px', sm: '0' }}
                      size='md'
                      name='code'
                      {...register('code')}
                      isInvalid={errors.code}
                      errorBorderColor='red.300'
                      isDisabled={!isCreate}
                    />
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.code?.message}
                    </Text>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Name
                    </FormLabel>
                    <Input
                      fontSize='sm'
                      ms='4px'
                      type='text'
                      placeholder='Name'
                      maxLength={250}
                      mb={errors.name?.message ? '5px' : { lg: '24px', sm: '0' }}
                      size='md'
                      name='name'
                      {...register('name')}
                      isInvalid={errors.name}
                      errorBorderColor='red.300'
                    />
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.name?.message}
                    </Text>
                  </FormControl>
                  <FormControl>
                    <Switch
                      id='isChecked'
                      defaultChecked={isCreate && true}
                      name='isActive'
                      {...register('isActive')}
                      size='sm'
                      ms='4px'
                    >
                      Active
                    </Switch>
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Keys
                    </FormLabel>
                    {paramTags.length > 0 && (
                      <Wrap spacing={2} mb={2}>
                        {paramTags.map((tag) => (
                          <WrapItem key={tag}>
                            <Tag size='md' colorScheme='blue' borderRadius='full'>
                              <TagLabel>{tag}</TagLabel>
                              <TagCloseButton onClick={() => handleRemoveParam(tag)} />
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                    <Flex gap={2}>
                      <Input
                        fontSize='sm'
                        ms='4px'
                        type='text'
                        placeholder='Enter key and press Enter or comma'
                        size='md'
                        value={paramInput}
                        onChange={(e) => setParamInput(e.target.value)}
                        onKeyDown={handleParamInputKeyDown}
                      />
                      <Button size='md' onClick={handleAddParam} colorScheme='blue' variant='outline'>
                        Add
                      </Button>
                    </Flex>
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <Button
                    colorScheme='blue'
                    mr={3}
                    type='submit'
                    isLoading={isSubmitting}
                  >
                    {isCreate ? 'Create' : 'Update'}
                  </Button>
                  <Button
                    onClick={() => {
                      onClose();
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                </ModalFooter>
              </ModalContent>
            </form>
          </Modal>
        </Flex>
      </CardHeader>
      <CardBody>
        {!checkForbidden(132) ? (
          <Flex justify='center' align='center' height='200px' color='gray.500'>
            You do not have permission to view this page.
          </Flex>
        ) : (
        <>
        <Card>
          <form
            id='template-search-form'
            onSubmit={handleSubmitSearch(onSubmitSearch)}
          >
            <Flex
              direction='row'
              alignItems='center'
              mb={3}
              gap={4}
              flexWrap={'wrap'}
            >
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel ms='4px' fontSize='xs' fontWeight='semibold'>
                  Template Name
                </FormLabel>
                <Input
                  _placeholder={{ color: 'gray.400' }}
                  variant='main'
                  type='text'
                  placeholder='Name'
                  w='300px'
                  size='sm'
                  fontSize='sm'
                  name='nameSearch'
                  {...registerSearch('nameSearch')}
                  _focus={{ borderColor: 'blue.500' }}
                />
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                  Status
                </FormLabel>
                <Select
                  ms='4px'
                  type='text'
                  defaultValue='Active'
                  size='sm'
                  name='statusSearch'
                  {...registerSearch('statusSearch')}
                  width='100px'
                >
                  {STATUS_OPTIONS.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Box>
            </Flex>
            <Flex justify='center' align='center' gap='8px' mt={3}>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<RxReset />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  onClick={() => {
                    resetSearch();
                  }}
                >
                  Reset
                </Button>
              </Box>
              <Box direction='row' alignItems='center' display='block'>
                <Button
                  leftIcon={<BsSearch />}
                  variant='primary'
                  w='88px'
                  h='33px'
                  type='submit'
                >
                  Search
                </Button>
              </Box>
            </Flex>
          </form>
        </Card>
        <SearchTable2
          tableData={newListTemplates}
          columnsData={columnsDataEmailTemplate}
          totalRow={totalRows.emailTemplates}
          edit={editTemplate}
          listPermissions={[134]}
          actionsActive={['edit']}
          loadData={(fetchDataOptions) => recallApi(fetchDataOptions)}
          isFetching={isFetching}
          refreshPagingDefault={refreshPagingDefault}
        />
        </>
        )}
      </CardBody>
    </Card>
  );
}

export default DataTableEmailTemplate;
